import type {
  CommandAck,
  CommandBody,
  GameProjection,
  HostCommand,
  Lobby,
} from '@lafayette/protocol';
import { SOCKET_PATH } from '@lafayette/protocol';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { io, type Socket } from 'socket.io-client';

export type ConnectionStatus = 'connecting' | 'connected' | 'reconnecting' | 'error';

export interface RoomConnection {
  status: ConnectionStatus;
  playerId: string | null;
  lobby: Lobby | null;
  projection: GameProjection | null;
  lastRejection: string | null;
  errorMessage: string | null;
  send: (body: CommandBody) => void;
  hostCommand: (command: HostCommand) => void;
}

function newKey(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/**
 * Opens the realtime seat connection and keeps the latest projection. All
 * reconnection is handled by socket.io; we simply re-attach on every connect.
 */
export function useRoom(roomId: string, resumeToken: string | null): RoomConnection {
  const [status, setStatus] = useState<ConnectionStatus>('connecting');
  const [playerId, setPlayerId] = useState<string | null>(null);
  const [lobby, setLobby] = useState<Lobby | null>(null);
  const [projection, setProjection] = useState<GameProjection | null>(null);
  const [lastRejection, setLastRejection] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const socketRef = useRef<Socket | null>(null);
  const versionRef = useRef(0);

  useEffect(() => {
    if (!resumeToken) return;
    const socket = io({ path: SOCKET_PATH, transports: ['websocket', 'polling'] });
    socketRef.current = socket;

    const attach = (): void => {
      setStatus('connected');
      socket.emit('attach', { roomId, resumeToken });
    };

    socket.on('connect', attach);
    socket.on('disconnect', () => setStatus('reconnecting'));
    socket.on('connect_error', () => setStatus('reconnecting'));
    socket.on('attached', (payload: { playerId: string; lobby: Lobby }) => {
      setPlayerId(payload.playerId);
      setLobby(payload.lobby);
      setErrorMessage(null);
    });
    socket.on('lobby', setLobby);
    socket.on('state', (next: GameProjection) => {
      versionRef.current = next.version;
      setProjection(next);
      setLastRejection(null);
    });
    socket.on('ack', (ack: CommandAck) => {
      if (!ack.ok) {
        setLastRejection(ack.reason);
        versionRef.current = ack.version;
      }
    });
    socket.on('error', (payload: { message: string }) => {
      setErrorMessage(payload.message);
      setStatus('error');
    });

    return () => {
      socket.removeAllListeners();
      socket.close();
      socketRef.current = null;
    };
  }, [roomId, resumeToken]);

  const send = useCallback(
    (body: CommandBody) => {
      const socket = socketRef.current;
      if (!socket || !playerId) return;
      setLastRejection(null);
      socket.emit('command', {
        idempotencyKey: newKey(),
        expectedVersion: versionRef.current,
        playerId,
        body,
      });
    },
    [playerId],
  );

  const hostCommand = useCallback((command: HostCommand) => {
    socketRef.current?.emit('host', command);
  }, []);

  return useMemo(
    () => ({ status, playerId, lobby, projection, lastRejection, errorMessage, send, hostCommand }),
    [status, playerId, lobby, projection, lastRejection, errorMessage, send, hostCommand],
  );
}
